import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Bar } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const PERIODS = ['today', 'yesterday', 'week'];
const PERIOD_LABELS = { today: 'Idag', yesterday: 'Igår', week: 'Vecka' };
const ZONES_ORDER = ['SE1', 'SE2', 'SE3', 'SE4', 'NO1', 'NO2', 'DK1', 'DK2', 'FI'];

const ZONE_COLORS = {
    SE1: '#59a14f', SE2: '#4e79a7', SE3: '#edc949', SE4: '#e15759',
    NO1: '#76b7b2', NO2: '#59a14f', DK1: '#f28e2b', DK2: '#ff9da7',
    FI: '#b07aa1',
};

const StatusBadge = ({ status }) => {
    const labels = { 'Net Exporter': 'Nettoexportör', 'Net Importer': 'Nettoimportör', 'Balanced': 'Balanserad' };
    const colors = {
        'Net Exporter': 'bg-green-500/20 text-green-400 border border-green-500/30',
        'Net Importer': 'bg-red-500/20 text-red-400 border border-red-500/30',
        'Balanced': 'bg-gray-500/20 text-gray-400 border border-gray-500/30',
    };
    return (
        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${colors[status] || colors['Balanced']}`}>
            {labels[status] || status}
        </span>
    );
};

export default function NetPositionsDashboard() {
    const [period, setPeriod] = useState('today');
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const fetchData = async (p) => {
        setLoading(true);
        setError(null);
        try {
            const res = await axios.get(`/api/net-positions?period=${p}`);
            setData(res.data);
        } catch (err) {
            setError(err.response?.data?.error || 'Kunde inte hämta nettopositioner');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchData(period); }, [period]);

    const buildBarChart = () => {
        if (!data?.summary) return null;

        const zones = ZONES_ORDER.filter(z => data.summary[z] !== undefined);
        if (zones.length === 0) return null;

        return {
            labels: zones,
            datasets: [{
                label: 'Avg Net Position (MW)',
                data: zones.map(z => Math.round(data.summary[z]?.average || 0)),
                backgroundColor: zones.map(z => {
                    const avg = data.summary[z]?.average || 0;
                    return avg > 0 ? '#22c55e80' : '#ef444480';
                }),
                borderColor: zones.map(z => {
                    const avg = data.summary[z]?.average || 0;
                    return avg > 0 ? '#22c55e' : '#ef4444';
                }),
                borderWidth: 1,
                borderRadius: 4,
            }]
        };
    };

    const buildHourlyChart = (zone) => {
        if (!data?.zones?.[zone]?.positions?.length) return null;
        const positions = data.zones[zone].positions;
        return {
            labels: positions.map((_, i) => `H${i + 1}`),
            datasets: [{
                label: `${zone} Net Position`,
                data: positions,
                backgroundColor: positions.map(v => v > 0 ? '#22c55e40' : '#ef444440'),
                borderColor: ZONE_COLORS[zone] || '#888',
                borderWidth: 1,
                borderRadius: 2,
            }]
        };
    };

    const barChart = buildBarChart();
    const [selectedZone, setSelectedZone] = useState('SE3');
    const hourlyChart = buildHourlyChart(selectedZone);

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: { display: false },
            tooltip: {
                callbacks: {
                    label: (ctx) => ` ${ctx.parsed.y.toLocaleString()} MW`
                }
            }
        },
        scales: {
            x: { ticks: { color: '#6b7280' }, grid: { color: 'rgba(255,255,255,0.05)' } },
            y: {
                ticks: { color: '#6b7280', callback: v => `${v.toLocaleString()} MW` },
                grid: { color: 'rgba(255,255,255,0.05)' }
            }
        }
    };

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-900 via-black to-gray-900 text-white p-6">
            <div className="max-w-6xl mx-auto">
                <h1 className="text-3xl font-bold mb-2">Nettopositioner</h1>
                <p className="text-gray-400 mb-6">
                    Dagsprognosens nettoposition per budzon · Positiv = export, Negativ = import
                </p>

                {/* Period Selector */}
                <div className="flex gap-2 mb-6">
                    {PERIODS.map(p => (
                        <button
                            key={p}
                            onClick={() => setPeriod(p)}
                            className={`px-4 py-1.5 rounded-lg text-sm capitalize transition-colors ${
                                period === p ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-300 hover:bg-white/20'
                            }`}
                        >
                            {p}
                        </button>
                    ))}
                </div>

                {loading && (
                    <div className="text-center py-20 text-gray-400">
                        <div className="animate-spin h-10 w-10 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
                        Hämtar nettopositioner...
                    </div>
                )}

                {error && (
                    <div className="bg-red-500/20 border border-red-500/40 rounded-lg p-4 text-red-300">{error}</div>
                )}

                {!loading && !error && data && (
                    <>
                        {/* Summary Grid */}
                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mb-6">
                            {ZONES_ORDER.filter(z => data.summary?.[z]).map(z => {
                                const s = data.summary[z];
                                return (
                                    <button
                                        key={z}
                                        onClick={() => setSelectedZone(z)}
                                        className={`bg-white/5 border rounded-xl p-3 text-left transition-colors ${
                                            selectedZone === z ? 'border-blue-500' : 'border-white/10 hover:border-white/30'
                                        }`}
                                    >
                                        <div className="flex items-center justify-between mb-2">
                                            <span className="font-semibold text-sm">{z}</span>
                                            <span className={`w-2 h-2 rounded-full ${s.average > 0 ? 'bg-green-500' : 'bg-red-500'}`} />
                                        </div>
                                        <p className={`text-lg font-bold ${s.average > 0 ? 'text-green-400' : 'text-red-400'}`}>
                                            {s.average > 0 ? '+' : ''}{Math.round(s.average).toLocaleString()}
                                        </p>
                                        <p className="text-xs text-gray-500">medel MW</p>
                                        <StatusBadge status={s.status} />
                                    </button>
                                );
                            })}
                        </div>

                        {/* Overview Bar Chart */}
                        {barChart && (
                            <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-6">
                                <h2 className="text-lg font-semibold mb-4">Genomsnittlig nettoposition per zon (MW)</h2>
                                <div className="h-64">
                                    <Bar data={barChart} options={chartOptions} />
                                </div>
                            </div>
                        )}

                        {/* Hourly Detail Chart */}
                        {hourlyChart && (
                            <div className="bg-white/5 border border-white/10 rounded-xl p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <h2 className="text-lg font-semibold">Timvisa positioner – {selectedZone}</h2>
                                    <div className="flex gap-2">
                                        {ZONES_ORDER.filter(z => data.zones?.[z]?.positions?.length).map(z => (
                                            <button
                                                key={z}
                                                onClick={() => setSelectedZone(z)}
                                                className={`px-2.5 py-1 rounded text-xs transition-colors ${
                                                    selectedZone === z ? 'bg-blue-600 text-white' : 'bg-white/10 text-gray-400 hover:bg-white/20'
                                                }`}
                                            >
                                                {z}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="h-56">
                                    <Bar data={hourlyChart} options={chartOptions} />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
